import React, { useState, useEffect } from "react";
import {Box, makeStyles, Typography} from "@material-ui/core";
import moment from "moment";
import { useIntl } from "react-intl";
import styled from "styled-components";

import {
  remoteAuthUserRequest,
  useAuthenticationContext, useAuthSession, UserActionTypeEnum,
} from "./context/UserContext";
import InternalErrorPage from "./screens/errors/500";
import {SignInScreen} from "./screens/signin/SignInScreen";
import {PageTitle} from "./components/page-title/PageTitle";
import {APP_NAME} from "./settings/constants";
import {Helmet} from "react-helmet";

const useStyles = makeStyles(
    (theme) => ({
      root: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        width: "100%",
        padding: 0,
        margin: 0,
        backgroundColor: theme.palette.background.paper,
        overflowX: "hidden",
      },
      wrapper: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      mainTitle: {
        fontSize: "1.5rem !important",
        fontWeight: theme.typography.fontWeightBold,
      },
      subTitle: {
        fontSize: "1rem !important",
        color: theme.palette.grey[600],
      },
    }),
    { name: "launcher-page-style", flip: null }
);

export const StyledBounceBall = styled("div")(({ theme }) => `
  position: relative;
  height: 38px;
  width: 16px;
  margin-bottom: 12px;

  &:before {
    position: absolute;
    content: '';
    display: block;
    top: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #F94F4F;
    transform-origin: 50%;
    animation: bounce 500ms alternate infinite ease;
  }

  @keyframes bounce {
    from {
      top: 40px;
      height: 2px;
      border-radius: 60px 60px 20px 20px;
      transform: scaleX(2);
    }
    35% {
      height: 16px;
      border-radius: 50%;
      transform: scaleX(1);
    }
    to {
      top: -40px;
    }
  }
`
);

interface LauncherState {
  loading: boolean;
  failure?: boolean;
  succeed?: boolean;
}

export const InitApp = (props: any, ..._rest: any[]) => {
  const classes = useStyles();
  const intl = useIntl();
  // State
  const [launcherState, setLauncherState] = useState(
    (): LauncherState => {
      return {
        loading: true,
        succeed: false,
        failure: false,
      };
    }
  );
  // Context
  const [authUser, token, dispatch] = useAuthenticationContext();
  const sessionExpiresAt = useAuthSession();

  const computePage = () => {
    const isConnected = Boolean(token !== null) && Boolean(authUser !== null);

    if (isConnected) {
      try {
        if(sessionExpiresAt <= moment().unix()) {
          dispatch({
            type: UserActionTypeEnum.logout,
          });
          window.location.replace(SignInScreen.routeName);
        } else {
          remoteAuthUserRequest(token, dispatch).then((user) => {
            setLauncherState({
              loading: false,
              succeed: user !== null,
              failure: user === null,
            });
          });
        }
      } catch (error) {
        console.dir(error);
        setLauncherState({
          loading: false,
          succeed: false,
          failure: true
        });
      }
    } else {
      new Promise((resolve) => {
        return setTimeout(resolve, 2000);
      }).then((e) => {
        setLauncherState({
          loading: false,
          succeed: true,
          failure: false
        });
      });
    }
  };

  useEffect(() => {
    computePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const render = () => {
    if (launcherState.succeed) {
      return props.children;
    }

    if (launcherState.failure) {
      return <InternalErrorPage />;
    }

    return (
        <>
          <Helmet>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
          </Helmet>
          <PageTitle
              withAppTitle
              title={intl.formatMessage({ id: "sharedScreen.welcome" })}
          />
          <Launcher />
        </>
    );
  };

  return render();
};

export const Launcher = () => {
  const classes = useStyles();

  const render = () => {
    return (
        <>
          <Box className={classes.root}>
            <Box component="div" style={{ padding: "0 20px" }}>
              <Box className={classes.wrapper}>
                <Box component="div" mr={2}>
                  <Typography noWrap component="p" className={classes.mainTitle}>
                    {APP_NAME}
                  </Typography>
                </Box>
                <StyledBounceBall />
              </Box>
            </Box>
          </Box>
        </>
    );
  };

  return render();
};

export default InitApp;
